/**
 * The bus rolling stock: New Flyer Xcelsior 40 ft (`BUS-XCEL-40`), the body every MTA
 * 40 ft local runs — XD40, XDE40, XN40, XE40, XHE40 all share it (`mta-assets/FLEET.md`).
 * The modelled variant is the XE40 CHARGE NG.
 *
 * The subway counterpart of this file is `r160Model.ts`, and the two are deliberately the
 * same shape: model URLs per LOD, a distance ladder, and the rule for which tiers earn the
 * physically-based shader. Authored in the sibling `mta-assets` repo
 * (`scripts/export_bus.py`); the app only holds copies under `public/models/bus/`.
 */
import { VENDORED_GLB_LOAD_OPTIONS } from './gltfLoadOptions'
import {
  paintDotField,
  signFont,
  signText,
  stampDotGrid,
  stampSignLabel,
  SIGN_AMBER,
  SIGN_GROUND,
  type SignPlacard,
} from './vehicleSigns'

export const XCEL40_MODELS = {
  // rev=6 is the fidelity pass against the MTA four-view elevation, and it is the first
  // one that moved L2 as well:
  //  • the tail-lamp stack reordered to amber / red / amber / clear — it was built from
  //    the New Flyer NG marketing render, which has a different order to this fleet;
  //  • the wheel split into a WHITE rim and a blue `MAT_WHEEL_CAP`, which were one
  //    silver material and so could only ever be one colour;
  //  • `MAT_PLATE` — the front and rear licence plates and the wide-turn placard, none
  //    of which the body carried at all. The front plate is NOT centred on the fascia:
  //    it rides on the belt band directly above the bus's left headlight, which is
  //    where the elevation puts it. The rear one is 14.3 × 6.9 in, a plate in its
  //    frame, rather than the squarer blank it was first cut as;
  //  • a centre high-mounted brake lamp, likewise missing;
  //  • and the NG render's turquoise trim recess on the nose demoted to `MAT_BODY`. It
  //    stood 0.92 in proud DEAD ON the centreline, which is where the agency roundel
  //    goes, so it occluded the mark entirely — see the note at its `add_front_plate`.
  // ⚠️ L2 moves too this time, unlike rev=5. The far tier folds every material into two,
  // so a material SPLIT leaves it byte-identical — but new geometry does not, and the
  // plates, placard and brake lamp all merge into `MAT_FAR_SHELL`.
  // rev=8: the DRIVER'S mirror was the curb-side one reflected, and the two corners of
  // this body do not carry the same installation. It hung at z 82.5–96.5, level with the
  // top of the windshield, 4–11 in forward of the bumper line — where the real one sits
  // beside the driver's own window on a bracket at the belt line. It moves down 26 in and
  // back 15, to z 56.5–70.5 at x 232.0–240.5, and its arm moves under it. The curb side is
  // unchanged and always was right: it is the one that was traced. All three tiers move —
  // the mirror survives into L2's merged shell, so even the far tier had it in the wrong
  // place. Sources and the three photo measurements are at `MIRROR_*_STREET` in
  // `mta-assets/scripts/blockout_bus_sides.py`.
  // rev=9: the DRIVER'S side was a window short. A `FORWARD` bay between the driver's
  // glass and the first passenger window had been cut out of `WINDOWS_LEFT` in a previous
  // pass, on the grounds that the aperture traced there (z 50.95–92.62, 41.7 in tall) was
  // the panel's own outline rather than an opening — which was true of those BOUNDS and
  // does not make the window absent. It left 1.673 m of dead wall in a flank whose every
  // other bay is on a ~53.5 in pitch, and `XE40_photo_mta_side_offside.png` shows an
  // ordinary transomed bay there. Restored at the measured x 130.24–182.86, taking its z
  // and transom from `FRONT_BAY` beside it so two adjacent bays share a header and a sill.
  // All three tiers move; the CURB side is byte-identical, and no livery constant does —
  // `MAT_LIVERY`, `MAT_LIVERY_GLASS` and both caps keep their bounds to the digit.
  L0: '/models/bus/BUS-XCEL-40_L0.glb?rev=9',
  L1: '/models/bus/BUS-XCEL-40_L1.glb?rev=9',
  L2: '/models/bus/BUS-XCEL-40_L2.glb?rev=9',
} as const

/**
 * The material a livery repaints, and the one surface on this body that carries texture
 * coordinates. Exported by `assign_livery_panel` in `mta-assets/scripts/export_bus.py`,
 * which moves the sidewall out of `MAT_BLOCKOUT_BODY` and unwraps it as a side elevation.
 *
 * Present at L0 and L1 only. L2 folds the whole bus into `MAT_FAR_SHELL` and its UVs are
 * stripped on export, so a livery reaches that tier as a base COLOUR — which is all a
 * 12–48 px bus can show of a wrap anyway.
 */
export const XCEL40_LIVERY_MATERIAL = 'MAT_LIVERY'
export const XCEL40_FAR_SHELL_MATERIAL = 'MAT_FAR_SHELL'
/**
 * Everything on the body that is NOT sidewall: the roof, the front cap, the wheel arches
 * and the skirt returns, all on one material. A livery cannot draw on it — the faces have
 * no meaningful UVs — but it must still COLOUR it, because a liveried bus drops the route
 * tint that used to hide the authored blockout grey (see `busBody` in `TransitMap.svelte`).
 * Paint the flank and leave this alone and the bus is blue with a grey roof and grey nose.
 */
export const XCEL40_SHELL_MATERIAL = 'MAT_BLOCKOUT_BODY'
/**
 * The rear end, split off `MAT_BLOCKOUT_BODY` by `assign_rear_cap` for exactly one reason:
 * an MTA bus's tail is YELLOW while its flanks and roof are blue, and the tail is not
 * sidewall, so the livery panel can never reach it. One flat colour, no artwork.
 */
export const XCEL40_REAR_CAP_MATERIAL = 'MAT_REAR_CAP'

/**
 * Which material carries each livery surface on this body — the whole contract between
 * `export_bus.py` and `liveryScenegraph`.
 *
 * The bus has all five: it is the vehicle full wraps are actually sold on, and the one
 * with skin at both ends to paint. `doors` and `windows` share the sidewall's unwrap, so
 * a livery that names them needs no extra artwork; `front` and `rear` are their own
 * elevations and need their own `drawFront`/`drawRear`.
 *
 * All five are present at L0 and L1 and none at L2, where the body folds into
 * `MAT_FAR_SHELL` with its UVs stripped and a livery arrives as `farColor` instead.
 */
export const XCEL40_LIVERY_SURFACES = {
  sidewall: XCEL40_LIVERY_MATERIAL,
  doors: 'MAT_LIVERY_DOOR',
  windows: 'MAT_LIVERY_GLASS',
  front: 'MAT_LIVERY_FRONT',
  rear: XCEL40_REAR_CAP_MATERIAL,
} as const
export type Xcel40Lod = keyof typeof XCEL40_MODELS

/** Same vendored-Draco requirement as the R160. Re-exported so call sites read locally. */
export const XCEL40_LOAD_OPTIONS = VENDORED_GLB_LOAD_OPTIONS

// ── The measured body, in metres, straight off the export ────────────────────────────
// Read from the exported GLB's own accessor bounds, not from the spec sheet, so anything
// laid out against the bus (the route badge, the Minion overlays) is placed against the
// mesh that actually draws. `mta-assets/exports/bus/BUS-XCEL-40/manifest.json` records the
// frame: +X forward, +Z up, origin on the ground plane at the longitudinal/lateral centre.
//
// ⚠️ The body is NOT centred on its origin: it runs -6.223 to +6.459, because the origin is
// the wheelbase datum rather than the midpoint of the bumpers. Anything that means "the
// front of the bus" must use XCEL40_FRONT_X, not half of XCEL40_LENGTH_M.
export const XCEL40_LENGTH_M = 12.682
export const XCEL40_FRONT_X = 6.459
export const XCEL40_REAR_X = -6.223
export const XCEL40_HALF_WIDTH_M = 1.56
export const XCEL40_ROOF_Z = 3.368

/**
 * The livery panel's own extent, in the same body metres — and it is NOT the body's.
 *
 * `assign_livery_panel` normalises u and v over the **panel's** bounding box rather than the
 * bus's, so these four figures are the only ones that convert a real position on the bus into
 * a position in the artwork. They are the exporter's own `LIVERY_PANEL x0/x1/z0/z1` log line,
 * cross-checked against the shipped GLB by decoding its MAT_LIVERY primitive: the panel is
 * 12.268 m × 2.642 m, so it stops ~0.2 m short of each bumper and ~0.42 m below the roof —
 * the shoulders where a panel stops facing sideways.
 *
 * ⚠️ **The two flanks get their own HALF of the image**, stacked, and they do not agree about
 * which end of the bus is which:
 *
 *   • image TOP half (v 0 → 0.5) is the bus's physical LEFT flank, its front at u = 0;
 *   • image BOTTOM half (v 0.5 → 1) is the RIGHT flank, its front at u = 1;
 *   • both are upright — v = 0 and v = 0.5 are both the panel's TOP edge, not its skirt.
 *
 * Measured, not read off the exporter: decoding the panel's positions against its TEXCOORD_0
 * puts 99.1 % of its vertices in the quadrant above (the rest are seam vertices shared with
 * the far flank at the skirt), with u and v linear in x and z to within 2e-4.
 *
 * `liveries.ts` draws ONE elevation and `liveryScenegraph.ts` fills both halves from it, so a
 * livery only ever works in the frame these two helpers define.
 */
export const XCEL40_PANEL = {
  rearX: -6.102,
  frontX: 6.166,
  skirtZ: 0.305,
  topZ: 2.947,
} as const

/**
 * The side window band, measured off `MAT_BLOCKOUT_WINDOW_FRAME` in the same GLB.
 *
 * A livery needs it because the band is NOT the livery's surface — glazing, frames and door
 * leaves are their own near-black materials — so artwork drawn across it is hidden by the
 * openings and only shows on the pillars between them. What is continuous wall is the
 * 0.62 m above the band (up to the panel's top edge) and the 1.06 m below it.
 */
export const XCEL40_WINDOW_BAND = { bottomZ: 1.362, topZ: 2.327 } as const

/** Along the panel: 0 at its rear edge, 1 at its front. The artwork's own x axis. */
export function xcel40PanelU(xMetres: number): number {
  return (xMetres - XCEL40_PANEL.rearX) / (XCEL40_PANEL.frontX - XCEL40_PANEL.rearX)
}

/**
 * Up the panel, WITHIN one flank's island: 0 at the panel's top edge, 1 at its skirt.
 * Increases downward, because that is the direction a canvas row index runs.
 */
export function xcel40PanelV(zMetres: number): number {
  return (XCEL40_PANEL.topZ - zMetres) / (XCEL40_PANEL.topZ - XCEL40_PANEL.skirtZ)
}

/**
 * How much bigger than life the bus is drawn.
 *
 * ⚠️ This is not 1. The bus is exaggerated on purpose, so that two of them on the same
 * corridor do not overlap into one smear at the zooms a rider actually uses: at zoom 14,
 * where the ladder drops to L2, a real 12.7 m body is under 2 px. The R160 is 1:1 and the
 * bus is not, which is a legibility decision, not an oversight.
 *
 * But it used to be **4.61×** — the on-map length of the procedural box this mesh replaced
 * (its half-length 5.0 × its 5.85 scale, doubled = 58.5 m), carried over verbatim so the
 * glTF swap moved nothing. That number was never chosen for the real body: 58.5 m is three
 * R160 cars, and a bus that outsizes half a train reads as a bug next to one. 1.5× is the
 * smallest exaggeration that still separates two buses at low zoom while keeping the body
 * roughly ONE subway car long (19.0 m vs 18.4 m), so the two vehicles read at plausible
 * relative size wherever they share a street.
 *
 * Everything laid out against the bus rides this same scale — the Minion overlays, the
 * floating route badge and its altitude — so the whole assembly shrinks together.
 */
export const XCEL40_SIZE_SCALE = 1.5
/** The on-map footprint that scale buys, for the comment above to be checkable. ~19.0 m. */
export const XCEL40_MAP_LENGTH_M = XCEL40_LENGTH_M * XCEL40_SIZE_SCALE

/**
 * Which body mesh to draw at this zoom, or `null` for none.
 *
 * A zoom ladder, not the per-vehicle projected-size policy the trains use
 * (`trainRenderLod.ts`). That policy exists because a train is EIGHT car instances whose
 * cost is worth measuring per consist; a bus is one instance, so the cheaper rule is the
 * right one and the extra projection per bus would cost more than it saved across a field
 * of them.
 *
 * There is no marker-only tier here on purpose: the bus has always drawn a mesh at every
 * zoom, and L2 at 321 triangles is the same order as the ~250-triangle box it replaces, so
 * the far end of the ladder is not a regression from what shipped before.
 */
export const XCEL40_MESH_L1_ZOOM = 14
export const XCEL40_MESH_L0_ZOOM = 16

export function xcel40LodForZoom(zoom: number): Xcel40Lod {
  if (zoom >= XCEL40_MESH_L0_ZOOM) return 'L0'
  return zoom >= XCEL40_MESH_L1_ZOOM ? 'L1' : 'L2'
}

/**
 * Whether this tier's GLB is worth the physically-based shader. Same contract as
 * `r160TierUsesPbr`, and the same reason: L2 is exported as ONE non-metallic material
 * (`MAT_FAR_SHELL`) chosen for deck's unlit default, while L0/L1 keep the authored
 * materials — including the metallic wheel hub — which need the lit path to read at all.
 * **The exporter's `LOD_MATERIAL_PLAN` and this function are one decision in two files.**
 */
export function xcel40TierUsesPbr(tier: Xcel40Lod): boolean {
  return tier !== 'L2'
}

// ── The destination signs ────────────────────────────────────────────────────────────
//
// Front, curb side and rear — the three a revenue MTA bus carries, and the three the
// body already models as decal targets on `MAT_SIGN_DISPLAY`. They are drawn the way
// the R160's rollsigns are (`r160Model.ts`): an amber dot matrix on a flat quad standing
// just proud of the panel, through `VehicleSideIconLayer`.
//
// ⚠️ ONLY THE FOLLOWED VEHICLE WEARS THEM. Every placard on this map — these three and
// the R160's whole cluster — is drawn for the boarded/watched vehicle and no other, on
// pure performance grounds: a placard is an instanced quad whose texture is a canvas,
// and the fleet-wide version of that cost measurably degraded the frame. The rule lives
// at the layer in `TransitMap.svelte`; this table is the same three signs either way.
//
// ⚠️ THERE IS NO STREET-SIDE SIGN, and its absence is the feature. An MTA bus signs the
// side a rider boards from and nothing else; a fourth sign on the traffic side would be
// a detail invented to fill a symmetry the real vehicle does not have.
//
// ── Where these numbers come from ────────────────────────────────────────────────────
//
// Unlike the R160 — whose placements are transcribed from its generator by hand, and say
// so — this body PUBLISHES its signs: `sign_panels()` in
// `mta-assets/scripts/blockout_bus_sides.py` returns all three as decal rectangles, and
// they are exported on `MAT_SIGN_DISPLAY`. Every figure below was read back off the
// SHIPPED GLB (decoding that primitive's Draco positions) and then cross-checked against
// the generator's own literals, which agree to the 0.03" the Draco quantisation allows:
//
//   front  z 96.30..108.40",  half width 34.00"      → FRONT_SIGN_Z / _HALF_WIDTH
//   rear   y -30.55..-10.95", z 103.18..112.64"      → REAR_SIGN_Y / _Z, less the bezel
//   side   x 141.71..179.01", z 80.56..89.99"        → the FORWARD bay, less the insets
//
// Both routes, independently, or the number is not trusted — the same rule the exporter
// applies to itself.
//
// ⚠️ **Two of the three panels are RAKED and an icon quad is not.** The front lies back
// ~19° with the windscreen and the rear sits on a face that rakes 6.3°. A flat quad has
// to stand at the SHALLOWEST station the panel spans — its most proud edge — or the
// depth test cuts it to a diagonal and half the sign is buried in the bodywork. That is
// the same rule `R160_CAB_DECAL_X` follows, and it costs the same thing: the front
// sign's top edge floats ~4.7" ahead of the panel, which is under a pixel at every zoom
// this is drawn at. `height` is therefore the panel's VERTICAL extent, not its slope
// length — a vertical quad standing in for a raked panel covers the panel's silhouette,
// and stretching the artwork over the extra 6% of slope would only shear it.

/** Clearance a sign quad stands proud of the panel it decals, in metres (0.5").
 *
 *  Not a hair. The R160's front bullet went INVISIBLE at 0.1" — a tenth of an inch on a
 *  vehicle this size is below a depth-buffer step, so the panel wins and nothing draws.
 *  0.5" is the figure that worked there and it is the figure used here. It is also safe
 *  on the side sign, whose quad sits inside the window reveal: the only thing in that
 *  quad's own footprint is the sign panel and the glass behind it (the frame ring is
 *  beside the aperture, not across it), and 0.5" still leaves it ~0.4" inside the skin. */
const XCEL40_SIGN_CLEARANCE = 0.0127

/**
 * The LED pitch, in metres (0.62"), and it is ONE pitch for the whole vehicle.
 *
 * A fleet buys one sign module, so the three displays do not each get a hand-picked row
 * count — the rows fall out of how tall each panel is. That gives 20 rows on the 12.1"
 * front, 15 on the 9.4" side and 15 on the 9.5" rear, which is both what the hardware
 * would do and, conveniently, the resolution the content needs. It is the same 0.62"
 * the R160's side sign runs (20 LEDs over a 12.3" sash), because it is the same range
 * real transit matrices are built in.
 */
const XCEL40_SIGN_LED_PITCH = 0.015748

/** Which of the three a placement is. Drives the LAYOUT, not the position. */
export type Xcel40SignFace = 'front' | 'side' | 'rear'

export type Xcel40SignPlacement = {
  face: Xcel40SignFace
  /** Along the bus from its origin toward the end the sign is on, in body metres.
   *  ⚠️ The origin is the wheelbase datum, not the midpoint — see `XCEL40_FRONT_X`. */
  x: number
  /** Out to the surface the sign rides. **+Y is the bus's LEFT**, which is why the two
   *  curb-side signs are negative: `TransitMap` offsets a placement along the vehicle's
   *  port direction, and the mesh draws with its own +Y to port, so the body frame and
   *  the placement frame are the same frame. */
  y: number
  /** Up from the ground plane to the panel's centre. */
  z: number
  /** The shader's reading direction — see `VehicleSideIconLayer`. +1 for the front face
   *  and for a LEFT flank, −1 for the rear face and for a RIGHT one. */
  side: number
  /** 0 lays the sign ALONG the bus (the flank), 1 across it (front and rear faces). */
  facing: 0 | 1
  /** The panel's vertical extent in metres. IconLayer sizes on height, so this is what
   *  is asked for and `aspect` is how the width follows. */
  height: number
  /** The panel's width ÷ its height — what the matrix canvas is drawn to. */
  aspect: number
  /** LEDs up the display, from the panel's height and the fleet's one pitch. */
  rows: number
}

function signPlacement(
  face: Xcel40SignFace,
  p: { x: number; y: number; z: number; side: number; facing: 0 | 1; width: number; height: number },
): Xcel40SignPlacement {
  return {
    face,
    x: p.x, y: p.y, z: p.z,
    side: p.side, facing: p.facing,
    height: p.height,
    aspect: p.width / p.height,
    rows: Math.round(p.height / XCEL40_SIGN_LED_PITCH),
  }
}

/**
 * All three signs, positioned. The bus's answer to `r160CarPlacards` — and a constant
 * rather than a function, because a bus is one body with no consist, no handedness and
 * no per-vehicle variation to resolve: every Xcelsior on the map wears exactly this.
 */
export const XCEL40_SIGN_PLACEMENTS: readonly Xcel40SignPlacement[] = Object.freeze([
  // Front: the full-width display in the top strip of the windshield. Centred on the
  // bus, so `side: 1` only picks the reading direction for someone stood in front of it.
  // `x` is the panel's BOTTOM edge (5.9893, its most proud station) plus the clearance —
  // see the rake note above.
  signPlacement('front', {
    x: 5.9893 + XCEL40_SIGN_CLEARANCE,
    y: 0,
    z: 2.59969,            // (96.30 + 108.40)/2"
    side: 1,
    facing: 1,
    width: 1.72720,        // 68.00" — 2 x FRONT_SIGN_HALF_WIDTH
    height: 0.30734,       // 12.10"
  }),
  // Curb side: the upper transom sash of the bay immediately aft of the front door.
  // `side: -1` because this is the vehicle's RIGHT flank, where forward is to the
  // viewer's right and the type therefore reads toward the nose.
  // ⚠️ `y` is the panel's FOOT (−1.256), not its head: the body's tumblehome closes the
  // wall with height, so the sash leans in ~0.2" over its own 9.4" and the foot is the
  // proud edge. Placing a flat quad on the nominal wall instead floats it off the mesh
  // at the bottom and cuts into the mesh at the top — the exact failure `sign_panels()`
  // exists upstream to prevent.
  signPlacement('side', {
    x: 4.073144,           // (141.71 + 179.01)/2"
    y: -1.256 - XCEL40_SIGN_CLEARANCE,
    z: 2.165985,           // (80.56 + 89.99)/2"
    side: -1,
    facing: 0,
    width: 0.947420,       // 37.30"
    height: 0.239522,      // 9.43"
  }),
  // Rear: a bolt-on housing at the TOP of the tail, hard against the curb side, directly
  // above the grille — NOT a centred plate. The street side up there carries the MTA
  // roundel and the fleet number, so this is a handed feature and `y` is negative for
  // the same reason the side sign's is. `x` is the panel's bottom edge (−6.0554), the
  // most proud station of a face that rakes forward with height.
  signPlacement('rear', {
    x: -6.0554 - XCEL40_SIGN_CLEARANCE,
    y: -0.527050,          // (−30.55 + −10.95)/2"
    z: 2.740914,           // (103.18 + 112.64)/2"
    side: -1,
    facing: 1,
    width: 0.497840,       // 19.60"
    height: 0.240284,      // 9.46"
  }),
])

/**
 * Whether this tier models the sign panel at all.
 *
 * ⚠️ This is NOT the legibility question. **Signs are drawn for the followed vehicle and
 * nothing else** — see the note over the sign layers in `TransitMap.svelte` — so by the
 * time this is asked there is at most one bus wearing them and no field-wide cost to
 * manage. What is left for this to answer is a correctness question: L2 folds the whole
 * body into `MAT_FAR_SHELL` and `MAT_SIGN_DISPLAY` does not survive it, so a decal at
 * that tier is a sign floating on a shell with no panel behind it.
 *
 * L0 and L1 both carry the panel, so the floor is `XCEL40_MESH_L1_ZOOM`.
 */
export function xcel40TierModelsSigns(tier: Xcel40Lod): boolean {
  return tier !== 'L2'
}

// ── Drawing one ──────────────────────────────────────────────────────────────────────

/**
 * How far the type may be condensed before it starts getting SHORTER instead.
 *
 * The same 0.82 the R160's side sign runs, and it was measured here rather than copied:
 * 0.55 was tried, to buy a long destination height instead of width, and it is WORSE.
 * Below about 0.8 the glyphs of a long name condense past the grid's own resolution and
 * the strokes merge — "SELECT BUS SERVICE" came out as a row of blobs at full height,
 * which is less readable than the same name at 60% height with its letterforms intact.
 * A matrix cannot render a stroke narrower than one LED, so width is the axis that has
 * a hard floor and height is the one that can give.
 */
const XCEL40_SIGN_CONDENSE = 0.82

/**
 * The share of a route-and-destination sign the ROUTE may take before the destination
 * starts paying for it.
 *
 * ⚠️ A bus route is not a subway bullet, and that is why the two fleets lay their signs
 * out differently. The R160 gives its route a SQUARE, because a subway bullet is one
 * glyph in a circle and always the same size. A bus id is text of wildly varying width —
 * "B1" against "M14A-SBS" is five characters' difference — so the route takes the width
 * it actually measures, and this only caps it, so that an SBS id cannot eat the
 * destination it is supposed to be introducing.
 *
 * Kept well under a third: the route is drawn at the display's FULL height, so even
 * three characters are wide, and the destination is the part with fifteen more to place.
 */
const XCEL40_ROUTE_MAX_SHARE = 0.30

/** The dark gap between route and destination, in LEDs. One was not a gap — the two ran
 *  together into a single word — and a real sign leaves the route visibly standing
 *  alone. Two reads as deliberate at every size this is drawn. */
const XCEL40_SIGN_GAP_LEDS = 2

/**
 * How squeezed a one-line destination may get before it is set on TWO lines instead.
 *
 * ⚠️ This is what makes the CURB-SIDE sign work at all, and the arithmetic is worth
 * having written down. That panel is 37.3 x 9.4" — 62 columns by 15 rows — and a
 * destination like "LAGUARDIA AIRPORT" is 21 characters with the route in front of it.
 * On one line each character gets under 2.4 columns, which is below the width a single
 * LED can draw a stroke in: the name renders as a bar, whatever is done with its height.
 *
 * Split it and both axes recover at once, because a half-length line at half the type
 * size is about a QUARTER the width — "LAGUARDIA" over "AIRPORT" comes out near
 * unsqueezed at 7 rows each, which is the classic 5x7 matrix cell and perfectly legible.
 *
 * The wide front strip almost never trips this (112 columns fits "SELECT BUS SERVICE" on
 * one line at ~0.55), so the two displays differ because their panels do, not because
 * they were given different rules.
 */
const XCEL40_SIGN_WRAP_BELOW = 0.55

/**
 * Split a label across two lines at the break nearest its middle, or return one line if
 * it has nowhere to break. Balancing the two matters more than it sounds: both lines are
 * drawn at the same size, so the LONGER one sets it, and breaking "LAGUARDIA AIRPORT"
 * after the first word beats breaking it anywhere else.
 *
 * A hyphen counts as a break, which is what lets the rear panel — 33 columns, the
 * narrowest display on the bus — set an SBS id as "M14A" over "SBS" instead of eight
 * merged characters. The delimiter is dropped either way; a line ending in a hanging
 * hyphen reads as a word that was cut, and this one was not.
 */
export function splitSignLabel(label: string): string[] {
  const mid = label.length / 2
  let best = -1
  for (let i = 0; i < label.length; i++) {
    if (label[i] !== ' ' && label[i] !== '-') continue
    if (best < 0 || Math.abs(i - mid) < Math.abs(best - mid)) best = i
  }
  return best < 0 ? [label] : [label.slice(0, best), label.slice(best + 1)]
}

/**
 * Stamp a label into a band, on one line or two — the whole of the fit decision in one
 * place, so the rear panel's route and the other two panels' destinations cannot drift
 * apart on how they handle an over-long string.
 */
function stampSignBlock(
  mctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  width: number,
  centreY: number,
  fontPx: number,
) {
  mctx.font = signFont(fontPx)
  const oneLineFit = width / Math.max(1, mctx.measureText(label).width)
  const lines = oneLineFit < XCEL40_SIGN_WRAP_BELOW ? splitSignLabel(label) : [label]
  if (lines.length === 1) {
    stampSignLabel(mctx, lines[0], x, width, centreY, fontPx, XCEL40_SIGN_CONDENSE)
    return
  }
  const lineH = fontPx / 2
  for (const [i, line] of lines.entries()) {
    stampSignLabel(
      mctx, line, x, width, centreY + (i - 0.5) * lineH, lineH, XCEL40_SIGN_CONDENSE,
    )
  }
}

const xcel40SignCache = new Map<string, SignPlacard>()

/**
 * One of the bus's destination signs, as an amber dot matrix.
 *
 * The front and curb-side displays carry route AND destination, which is what a rider
 * reads from the kerb. The rear carries the ROUTE ONLY — not a simplification but what
 * the panel is: 19.6 x 9.5", barely twice as wide as it is tall, and MTA signs it with
 * the route number so a following driver can identify the bus.
 *
 * `rows` and `aspect` both come from the placement rather than being picked here, so a
 * display's resolution and its shape stay facts about the panel, not about the painter.
 */
export function createXcel40Sign(
  placement: Xcel40SignPlacement,
  routeId: string,
  destination: string,
): SignPlacard {
  const route = signText(routeId)
  const dest = placement.face === 'rear' ? '' : signText(destination) || 'IN SERVICE'
  const key = `${placement.face}:${route}:${dest}`
  const cached = xcel40SignCache.get(key)
  if (cached) return cached

  const { rows, aspect } = placement
  const pitch = 4
  const canvas = document.createElement('canvas')
  canvas.height = (rows + 1) * pitch
  canvas.width = Math.round(canvas.height * aspect)
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = SIGN_GROUND
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const cols = Math.floor(canvas.width / pitch) - 1
  const ox = (canvas.width - cols * pitch) / 2 + pitch / 2
  const oy = (canvas.height - rows * pitch) / 2 + pitch / 2
  paintDotField(ctx, ox, oy, cols, rows, pitch)

  // Route and destination go into ONE mask so they land on ONE grid — the same reason
  // the R160's side sign does: two stamps at two origins let one's dots fall between the
  // other's, and that seam is exactly what makes a single display read as two signs
  // bolted together.
  const sample = 4
  const mask = document.createElement('canvas')
  mask.width = cols * sample
  mask.height = rows * sample
  const mctx = mask.getContext('2d', { willReadFrequently: true })!
  mctx.textAlign = 'center'
  mctx.textBaseline = 'middle'
  mctx.fillStyle = '#fff'

  const centreY = mask.height / 2
  const fontPx = rows * sample
  if (!dest) {
    // Rear: the route alone, filling the panel with a half-LED margin each side.
    stampSignBlock(mctx, route, sample * 0.5, mask.width - sample, centreY, fontPx)
  } else {
    // The route takes the width it measures at full height, capped so it cannot crowd
    // the destination out; the destination gets what is left after a one-LED gap.
    mctx.font = signFont(fontPx)
    const routeW = Math.min(mctx.measureText(route).width, mask.width * XCEL40_ROUTE_MAX_SHARE)
    // Through the same block stamper as everything else, so a long id wraps rather than
    // condensing into a bar: on the narrow curb-side panel "M14A-SBS" gets ~18 columns,
    // which is 2.2 per character on one line and a legible 4.5 on two.
    stampSignBlock(mctx, route, sample * 0.5, routeW, centreY, fontPx)

    // The destination gets everything left after the gap — on one line if it can hold
    // its letterforms there, on two if it cannot. See XCEL40_SIGN_WRAP_BELOW.
    const destX = sample * 0.5 + routeW + sample * XCEL40_SIGN_GAP_LEDS
    stampSignBlock(mctx, dest, destX, mask.width - destX - sample * 0.5, centreY, fontPx)
  }

  stampDotGrid(ctx, mask, cols, rows, sample, ox, oy, pitch, SIGN_AMBER)
  const result = { url: canvas.toDataURL(), width: canvas.width, height: canvas.height }
  xcel40SignCache.set(key, result)
  return result
}
